<div class="merge-individuals-dialog">
  <div class="dialog-header">
    <div>
      <h3 class="title">Merge individuals</h3>
      <p class="subtitle">
        Tick the record that is the same person, then choose the one to keep. Only two records can be merged at
        a time; merge any others separately. Their roles and documents move to the kept record, and the better
        KYC result moves with them. KYC and privacy history stay on each record. This cannot be undone.
      </p>
    </div>
    <div class="avatar-stack">
      @for (initials of retiringInitials; track $index) {
        <span class="avatar discard">{{ initials }}</span>
      }
      @if (retiringInitials.length) {
        <span class="mdi mdi-arrow-right"></span>
      }
      @if (survivorInitials) {
        <span class="avatar keep">{{ survivorInitials }}</span>
      }
    </div>
  </div>

  <mat-dialog-content>
    <mat-radio-group [value]="survivorId" (change)="onSelectSurvivor($event)">
      <table class="merge-table">
        <thead>
          <tr>
            <th class="col-pick"></th>
            <th>Name</th>
            <th>Date of Birth</th>
            <th>Signer Role</th>
            <th class="col-icon">Status</th>
            <th class="col-icon">Consent</th>
            <th class="col-keep">Keep</th>
          </tr>
        </thead>
        <tbody>
          @for (candidate of candidates; track candidate.id) {
            <tr class="candidate"
                [class.selected]="isParticipant(candidate.id)"
                [class.survivor]="candidate.id === survivorId && isParticipant(candidate.id)"
                [class.discarded]="isDiscarded(candidate.id)"
                [class.locked]="isLocked(candidate.id)"
                matTooltip="Untick a record to choose this one"
                [matTooltipDisabled]="!isLocked(candidate.id)"
                (click)="onRowClick(candidate.id)">
              <td class="col-pick">
                <mat-checkbox [checked]="isParticipant(candidate.id)"
                              [disabled]="isLocked(candidate.id)"
                              (click)="$event.stopPropagation()"
                              (change)="onToggleParticipant(candidate.id, $event)"></mat-checkbox>
              </td>
              <td>
                <div class="name-line">
                  <span class="name">{{ candidate.name }}</span>
                  @if (candidate.id === survivorId && isParticipant(candidate.id) && participantIds.length >= 2) {
                    <span class="chip keep">Keeping</span>
                  }
                </div>
                @if (mismatchReasons[candidate.id]; as reasons) {
                  <div class="mismatch-line">
                    <span class="mdi mdi-alert-outline"></span>Different {{ reasons.join(' and ') }} from the kept record
                  </div>
                }
                @for (role of candidate.roles; track role) {
                  <div class="role">{{ role }}</div>
                }
              </td>
              <td class="nowrap" [class.mismatch]="mismatchReasons[candidate.id]?.includes('date of birth')">{{ candidate.dob }}</td>
              <td>{{ candidate.signerRole | signerRole }}</td>
              <td class="col-icon">
                @if (candidate.kyc; as kyc) {
                  <span class="mdi status-icon {{ kyc.icon }} {{ kyc.styleClass }}" [matTooltip]="kyc.tooltip"></span>
                }
              </td>
              <td class="col-icon">
                <span class="mdi status-icon {{ candidate.consent.icon }} consent {{ candidate.consent.styleClass }}"
                      [matTooltip]="candidate.consent.tooltip"></span>
              </td>
              <td class="col-keep" (click)="$event.stopPropagation()">
                @if (isParticipant(candidate.id)) {
                  <mat-radio-button [value]="candidate.id" [attr.aria-label]="'Keep ' + candidate.name"></mat-radio-button>
                }
              </td>
            </tr>
          }
        </tbody>
      </table>
    </mat-radio-group>

    @if (hasIdentityMismatch) {
      <div class="mismatch-warning">
        <span class="mdi mdi-alert-circle-outline"></span>
        These records do not look like the same person as the one you are keeping
      </div>
    }
    @if (kycMismatch) {
      <div class="mismatch-warning kyc-mismatch">
        <span class="mdi mdi-alert-circle-outline"></span>
        {{ kycMismatch }} It will be sent to Under review.
      </div>
    }

    @if (result) {
      <section class="result">
        <h4 class="section-title">
          <span class="mdi mdi-arrow-down"></span>After the merge
        </h4>
        <div class="result-card">
          <div class="result-head">
            <span class="avatar keep">{{ result.initials }}</span>
            <div class="result-name">
              <div class="name">{{ result.name }}</div>
              <div class="chips">
                @if (result.isPrimaryContact) {
                  <tag-box [state]="'Primary Contact'"></tag-box>
                }
              </div>
            </div>
          </div>

          <dl class="facts">
            <div><dt>Date of birth</dt><dd>{{ result.dob || '–' }}</dd></div>
            <div><dt>Signer role</dt><dd>{{ (result.signerRole | signerRole) || '–' }}</dd></div>
            <div>
              <dt>KYC Status</dt>
              <dd>
                @if (result.kyc; as kyc) {
                  <span class="mdi status-icon {{ kyc.icon }} {{ kyc.styleClass }}"></span>{{ kyc.tooltip }}
                } @else {
                  Not started
                }
              </dd>
            </div>
            <div>
              <dt>Consent</dt>
              <dd>
                <span class="mdi status-icon {{ result.consent.icon }} consent {{ result.consent.styleClass }}"></span>{{ result.consent.tooltip }}
              </dd>
            </div>
          </dl>

          @if (result.missingDetails.length) {
            <div class="missing-details">
              <span class="mdi mdi-alert-circle-outline"></span>
              Will still need attention, missing: {{ result.missingDetails.join(', ') }}
            </div>
          }
          @if (result.needsNewConsent) {
            <div class="missing-details">
              <span class="mdi mdi-alert-circle-outline"></span>
              The kept record has no signed privacy consent; a new form will need to be sent
            </div>
          }

          <div class="block">
            <div class="block-title">Company roles</div>
            @for (role of result.companyRoles; track role.label) {
              <div class="company-role">
                <span>{{ role.label }}</span>
                @if (role.added) {
                  <span class="chip added">added</span>
                }
              </div>
            }
          </div>

          @if (profilePreview.length) {
            <div class="block">
              <div class="block-title">Personal details that differ</div>
              <table class="profile-table">
                <thead>
                  <tr><th>Field</th><th>Kept</th><th class="discarded">Discarded</th></tr>
                </thead>
                <tbody>
                  @for (row of profilePreview; track row.label) {
                    <tr>
                      <td class="field">{{ row.label }}</td>
                      <td class="kept">
                        @if (row.result) {
                          {{ row.result }}
                          @if (row.filled) {
                            <span class="chip added">filled in</span>
                          }
                        } @else {
                          <span class="blank">Left blank, records disagree</span>
                        }
                      </td>
                      <td class="discarded">{{ row.discarded.length ? row.discarded.join(', ') : '–' }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }

          @if (salesforceContacts.length) {
            <div class="salesforce-note">
              <span class="mdi mdi-information-outline"></span>
              <span>
                {{ salesforceContacts.length }} Salesforce contacts:
                @for (contact of salesforceContacts; track contact.id; let last = $last) {
                  {{ contact.id }} ({{ contact.name }}){{ contact.kept ? ' is kept' : '' }}{{ last ? '.' : ';' }}
                }
              </span>
            </div>
          }
        </div>
      </section>
    }
  </mat-dialog-content>

  <div class="dialog-footer">
    @if (hasMismatch) {
      <mat-checkbox [checked]="confirmedSamePerson" (change)="onConfirmSamePerson($event)">
        <span class="confirm-text">I have checked these are the same person</span>
      </mat-checkbox>
    }
    <span class="spacer"></span>
    <button mat-stroked-button (click)="onCancel()">Cancel</button>
    <button mat-flat-button [disabled]="!canSubmit" (click)="onSubmit()">
      Merge {{ participantIds.length === 2 ? '2 records' : '' }}
    </button>
  </div>
</div>
